// src/components/AuthPage.js
import React, { useState, useEffect } from "react";
import { auth } from "../firebase";
import {
    signInWithEmailAndPassword,
    signInWithPhoneNumber,
    RecaptchaVerifier,
    GoogleAuthProvider,
    signInWithPopup,
} from "firebase/auth";
import { useNavigate } from "react-router-dom";
import "../styles/auth.css";

const AuthPage = () => {
    const [authMethod, setAuthMethod] = useState("email"); // "email", "phone", or "oauth"
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [phone, setPhone] = useState("");
    const [otp, setOtp] = useState("");
    const [confirmationResult, setConfirmationResult] = useState(null);
    const [error, setError] = useState("");
    const [showOtpInput, setShowOtpInput] = useState(false);
    const navigate = useNavigate();

    useEffect(() => {
        // Initialize reCAPTCHA for phone authentication
        if (authMethod === "phone") {
            // Clean up any existing reCAPTCHA instance
            if (window.recaptchaVerifier) {
                window.recaptchaVerifier.clear();
                window.recaptchaVerifier = null;
            }

            try {
                window.recaptchaVerifier = new RecaptchaVerifier(
                    auth,
                    "recaptcha-container",
                    {
                        size: "normal", // Use "invisible" in production
                        callback: () => {
                            console.log("reCAPTCHA verified");
                        },
                        "expired-callback": () => {
                            setError("reCAPTCHA expired. Please try again.");
                        },
                    }
                );
                window.recaptchaVerifier.render().catch((err) => {
                    setError("Failed to render reCAPTCHA: " + err.message);
                });
            } catch (err) {
                setError("Failed to initialize reCAPTCHA: " + err.message);
            }

            // Cleanup on unmount
            return () => {
                if (window.recaptchaVerifier) {
                    window.recaptchaVerifier.clear();
                    window.recaptchaVerifier = null;
                }
            };
        }
    }, [authMethod]);

    const handleEmailLogin = async (e) => {
        e.preventDefault();
        setError("");
        try {
            await signInWithEmailAndPassword(auth, email, password);
            navigate("/chat");
        } catch (err) {
            setError(err.message);
        }
    };

    const handlePhoneLogin = async (e) => {
        e.preventDefault();
        setError("");
        if (!showOtpInput) {
            // Send OTP
            try {
                if (!window.recaptchaVerifier) {
                    throw new Error("reCAPTCHA not initialized");
                }
                const confirmation = await signInWithPhoneNumber(
                    auth,
                    phone,
                    window.recaptchaVerifier
                );
                setConfirmationResult(confirmation);
                setShowOtpInput(true);
            } catch (err) {
                setError(err.message);
                // Retry rendering reCAPTCHA if it fails
                if (err.message.includes("auth/requests-from-referer")) {
                    setTimeout(() => {
                        setAuthMethod("phone"); // Re-trigger useEffect to retry
                    }, 2000);
                }
            }
        } else {
            // Verify OTP
            try {
                await confirmationResult.confirm(otp);
                navigate("/chat");
            } catch (err) {
                setError(err.message);
            }
        }
    };

    const handleOAuthLogin = async (providerType) => {
        setError("");
        try {
            let provider;
            if (providerType === "google") {
                provider = new GoogleAuthProvider();
            }
            // Add more providers here (e.g., Facebook, GitHub) if needed
            // else if (providerType === "facebook") {
            //     provider = new FacebookAuthProvider();
            // }

            if (!provider) {
                throw new Error("Unsupported OAuth provider");
            }

            await signInWithPopup(auth, provider);
            navigate("/chat");
        } catch (err) {
            setError(err.message);
        }
    };

    return (
        <div className="auth-container">
            <div className="auth-box">
                    <button
            className="auth-close-btn"
            onClick={() => navigate(-1)}
            aria-label="Close login"
        >
            ✕
        </button>
                <h2>Login to Routechef</h2>
                <div className="auth-toggle">
                    <button
                        className={authMethod === "email" ? "active" : ""}
                        onClick={() => {
                            setAuthMethod("email");
                            setError("");
                            setShowOtpInput(false);
                        }}
                    >
                        Email
                    </button>
                    <button
                        className={authMethod === "phone" ? "active" : ""}
                        onClick={() => {
                            setAuthMethod("phone");
                            setError("");
                            setShowOtpInput(false);
                        }}
                    >
                        Phone
                    </button>
                    <button
                        className={authMethod === "oauth" ? "active" : ""}
                        onClick={() => {
                            setAuthMethod("oauth");
                            setError("");
                            setShowOtpInput(false);
                        }}
                    >
                        OAuth
                    </button>
                </div>

                {authMethod === "email" ? (
                    <form onSubmit={handleEmailLogin} className="auth-form">
                        <input
                            type="email"
                            placeholder="Email"
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            required
                        />
                        <input
                            type="password"
                            placeholder="Password"
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                            required
                        />
                        <button type="submit">Login with Email</button>
                    </form>
                ) : authMethod === "phone" ? (
                    <form onSubmit={handlePhoneLogin} className="auth-form">
                        {!showOtpInput ? (
                            <>
                                <input
                                    type="tel"
                                    placeholder="Phone Number (e.g., +918655234915)"
                                    value={phone}
                                    onChange={(e) => setPhone(e.target.value)}
                                    required
                                />
                                <div id="recaptcha-container"></div>
                                <button type="submit">Send OTP</button>
                            </>
                        ) : (
                            <>
                                <input
                                    type="text"
                                    placeholder="Enter OTP"
                                    value={otp}
                                    onChange={(e) => setOtp(e.target.value)}
                                    required
                                />
                                <button type="submit">Verify OTP</button>
                            </>
                        )}
                    </form>
                ) : (
                    <div className="oauth-options">
                        <button
                            className="oauth-btn google-btn"
                            onClick={() => handleOAuthLogin("google")}
                        >
                            <img src="/google-icon.svg" alt="Google Icon" className="oauth-icon" />
                            Login with Google
                        </button>
                        {/* Add more OAuth providers here */}
                        {/* <button
                            className="oauth-btn facebook-btn"
                            onClick={() => handleOAuthLogin("facebook")}
                        >
                            <img src="/facebook-icon.svg" alt="Facebook Icon" className="oauth-icon" />
                            Login with Facebook
                        </button> */}
                    </div>
                )}

                {error && <p className="error-message">{error}</p>}
            </div>
        </div>
    );
};

export default AuthPage;